import { api } from './api.ts';
import type { Repo } from './repo.ts';
import { isInstalled, isIOS } from './platform.ts';

/**
 * Lock-screen "Rest done" alerts (ADR-0006, ui_ux T05), iPhone and Android alike. On iOS, web push only
 * reaches the installed Home Screen app; Android browsers allow it in a normal tab. Permission must come
 * from a tap. The alarm lives in the Worker's Durable Object.
 */
const KEY = 'repwise.push';

export type PushState = 'unsupported' | 'needs_install' | 'off' | 'denied' | 'on';


export function pushState(): PushState {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return isIOS() && !isInstalled() ? 'needs_install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    return localStorage.getItem(KEY) === 'on' && Notification.permission === 'granted' ? 'on' : 'off';
  } catch {
    return 'off';
  }
}

function keyBytes(b64u: string): Uint8Array {
  const pad = b64u.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64u.length + 3) % 4);
  return Uint8Array.from(atob(pad), (c) => c.charCodeAt(0));
}

/** Call from a tap. Returns the resulting state. */
export async function enablePush(repo: Repo): Promise<PushState> {
  if (pushState() === 'unsupported' || pushState() === 'needs_install') return pushState();
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return pushState();
  const res = await api(repo, '/push/vapid-public-key', {}, false);
  if (!res?.ok) throw new Error('Alerts need a connection to set up. Try again online.');
  const { key } = (await res.json()) as { key: string };
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) as BufferSource }));
  const saved = await api(repo, '/push/subscribe', { method: 'POST', body: JSON.stringify({ subscription: sub.toJSON() }) });
  if (!saved?.ok) throw new Error('Sign in to turn on rest alerts.');
  localStorage.setItem(KEY, 'on');
  return 'on';
}

export async function disablePush(repo: Repo): Promise<PushState> {
  localStorage.removeItem(KEY);
  await api(repo, '/push/subscribe', { method: 'DELETE' });
  const reg = await navigator.serviceWorker.ready;
  await (await reg.pushManager.getSubscription())?.unsubscribe();
  return pushState();
}

/** Fire-and-forget: ask the Worker to push when this rest ends. The server computes the end time. */
export function scheduleRestAlert(repo: Repo, durationS: number, body: string): void {
  if (pushState() !== 'on' || durationS <= 0) return;
  void api(repo, '/timer/schedule', { method: 'POST', body: JSON.stringify({ durationS, title: 'Rest done', body }) });
}

export function cancelRestAlert(repo: Repo): void {
  if (pushState() !== 'on') return;
  void api(repo, '/timer/cancel', { method: 'POST' });
}

/** J19: the next-workout reminder, at a time this phone computed (its local morning). One per user; a new one replaces it. */
export function scheduleReminder(repo: Repo, at: number, body: string): void {
  if (pushState() !== 'on' || at <= Date.now()) return;
  void api(repo, '/timer/reminder', { method: 'POST', body: JSON.stringify({ at, title: 'Workout today', body }) });
}

export function cancelReminder(repo: Repo): void {
  if (pushState() !== 'on') return;
  void api(repo, '/timer/reminder', { method: 'DELETE' });
}

/** 08:00 local time on a YYYY-MM-DD day (this device's clock and zone). */
export const morningOf = (iso: string, hour = 8) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d, hour, 0, 0).getTime();
};

/** Weekly R2 backup, triggered on open; the Worker skips it if one is recent (ADR-0011 revised). */
export function backupIfDue(repo: Repo): void {
  void api(repo, '/backup', { method: 'POST' });
}
